import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, MessageSquare, Clock, Pin, Tag, Users, Calendar } from 'lucide-react';
import { api, formatApiError } from '../lib/api';
import InternalAccessGuard from '../components/InternalAccessGuard';
import { normalizeHtml } from '../lib/htmlText';
import { fullDate } from '../components/forum/forumUtils';

function DetailInner() {
  const { lang, id } = useParams();
  const isEs = lang !== 'en';
  const hubBase = `/${lang}/${isEs ? 'foro' : 'forum'}`;
  const [aviso, setAviso] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get(`/internal/avisos/${id}`)
      .then(({ data }) => setAviso(data))
      .catch((e) => { setErr(formatApiError(e.response?.data?.detail) || 'Error'); setAviso(false); });
  }, [id]);

  if (aviso === null) {
    return <div className="min-h-[60vh] pt-24 flex items-center justify-center"><Loader2 className="animate-spin text-cmic-500" size={30} /></div>;
  }
  if (aviso === false) {
    return (
      <div className="min-h-[60vh] pt-24 max-w-3xl mx-auto px-4">
        <Link to={`${hubBase}?tab=avisos`} className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-charcoal-800 mb-6"><ArrowLeft size={16} /> {isEs ? 'Volver al tablón' : 'Back to board'}</Link>
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{err || (isEs ? 'Aviso no encontrado' : 'Notice not found')}</div>
      </div>
    );
  }

  return (
    <article data-testid="aviso-detail-page" className="min-h-[70vh] bg-charcoal-50 pt-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <Link
          to={`${hubBase}?tab=avisos`}
          data-testid="aviso-detail-back"
          className="inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-charcoal-800 mb-6 transition-colors"
        >
          <ArrowLeft size={16} aria-hidden="true" /> {isEs ? 'Volver al tablón' : 'Back to board'}
        </Link>

        <div className="flex items-center gap-2 flex-wrap mb-3">
          {aviso.is_pinned && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.1em] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
              <Pin size={10} aria-hidden="true" /> {isEs ? 'Fijado' : 'Pinned'}
            </span>
          )}
          {aviso.category && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.1em] bg-cmic-50 text-cmic-700 px-2 py-0.5 rounded-full">
              <Tag size={10} aria-hidden="true" /> {aviso.category}
            </span>
          )}
          {aviso.audience && aviso.audience !== 'all' && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.1em] bg-charcoal-100 text-charcoal-600 px-2 py-0.5 rounded-full">
              <Users size={10} aria-hidden="true" /> {aviso.audience === 'admins' ? (isEs ? 'Solo admins' : 'Admins only') : (isEs ? 'Solo consejo' : 'Council only')}
            </span>
          )}
        </div>

        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-charcoal-900 leading-tight">{aviso.title}</h1>

        <div className="flex items-center gap-3 flex-wrap mt-4 text-sm text-charcoal-500 font-medium">
          <span className="w-8 h-8 rounded-full bg-cmic-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0" aria-hidden="true">
            {(aviso.author_name || 'C').charAt(0).toUpperCase()}
          </span>
          <span className="text-charcoal-700">{aviso.author_name || 'CMIC'}</span>
          <span className="text-charcoal-300">·</span>
          <span className="inline-flex items-center gap-1"><Calendar size={13} aria-hidden="true" /> {fullDate(aviso.created_at)}</span>
          {aviso.expires_at && (
            <span className="inline-flex items-center gap-1 text-charcoal-400"><Clock size={13} aria-hidden="true" /> {isEs ? 'Expira' : 'Expires'} {aviso.expires_at}</span>
          )}
        </div>

        {aviso.image_url && (
          <div className="w-full rounded-2xl overflow-hidden mb-8 mt-6 max-h-[60vh]">
            <img src={aviso.image_url} alt={aviso.title} className="w-full max-h-[60vh] object-cover" />
          </div>
        )}

        <div className="mt-8 bg-white rounded-2xl border border-charcoal-200 p-6 md:p-8 shadow-sm">
          {aviso.body_html ? (
            <div className="forum-rich-content break-words" dangerouslySetInnerHTML={{ __html: normalizeHtml(aviso.body_html) }} />
          ) : (
            <p className="text-charcoal-400 italic">{isEs ? 'Este aviso no tiene contenido.' : 'This notice has no content.'}</p>
          )}
        </div>

        {aviso.forum_thread_id && (
          <div className="mt-12 pt-8 border-t border-charcoal-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-lg font-bold tracking-tight text-charcoal-800">{isEs ? '¿Quieres opinar?' : 'Want to weigh in?'}</p>
              <p className="text-sm text-charcoal-500 mt-0.5">{isEs ? 'Únete a la conversación sobre este aviso en el foro.' : 'Join the discussion about this notice in the forum.'}</p>
            </div>
            <Link
              to={`${hubBase}/t/${aviso.forum_thread_id}`}
              data-testid="aviso-comment-cta"
              className="inline-flex items-center justify-center gap-2 bg-cmic-600 text-white hover:bg-cmic-700 rounded-lg px-6 py-3 text-sm font-semibold transition-colors flex-shrink-0"
            >
              <MessageSquare size={16} aria-hidden="true" /> {isEs ? 'Comentar sobre este aviso' : 'Comment on this notice'}
            </Link>
          </div>
        )}
      </div>
    </article>
  );
}

export default function AvisoDetailPage() {
  return (
    <InternalAccessGuard>
      <DetailInner />
    </InternalAccessGuard>
  );
}
